"use client";

import { util } from "@/common/theme";

import { mediaMobile } from "@/mono/lib/responsive";
import { useGlobalStore } from "@/mono/store/global";
import BorderedIcon from "components/BorderedIcon";
import SplitText from "mono/components/SplitText";
import { useMemo } from "react";
import styled from "styled-components";
import NineGLImageElement from "../../gl/NineGLImage/NineGLImageElement";
import { convertMultiParagraphToNineFormat } from "./MultiLineTextComponent";

export const columnRowTypeNames = {
  image: "ImagecolumnRecord",
  text: "TextcolumnRecord",
  empty: "EmptycolumnRecord",
};

function ColumnTextItem({ text, width, mobileWidth, cta, align = "center" }) {
  const formattedParagraph = convertMultiParagraphToNineFormat(text);

  const applyMarginBottom = useMemo(() => {
    if (mobileWidth === 100) return true;
    return false;
  }, [width]);

  const alignMap = {
    top: "flex-start",
    center: "center",
    bottom: "flex-end",
  };

  return (
    <StyledColumnTextItem
      $applyMarginBottom={applyMarginBottom}
      $flexAlign={alignMap[align]}
    >
      <div className="column-text-item__inner">
        {formattedParagraph.map((p, i) => (
          <SplitText key={i} tag={p.tag} animateOnScroll={true}>
            {p.text}
          </SplitText>
        ))}
        {cta && (
          <BorderedIcon
            dontTriggerPageTransition={cta.isExternal}
            className="column-text-item__cta"
            text={cta.text}
            href={cta.url}
          />
        )}
      </div>
    </StyledColumnTextItem>
  );
}

const StyledColumnTextItem = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  width: 100%;
  height: 100%;

  ${({ $flexAlign }) =>
    $flexAlign &&
    `
    justify-content: ${$flexAlign};
  `}

  a {
    width: fit-content;
    margin-left: 250rem;
    margin-top: 50rem;
  }

  ${({ $applyMarginBottom }) =>
    $applyMarginBottom &&
    `
    padding-bottom: 100rem;
  `}

  .column-text-item__inner {
    width: 100%;
    height: auto;
    display: flex;
    flex-flow: column nowrap;
    gap: 50rem;
  }
`;

export default function ColumnRow({
  data,
  height = "1400rem",
  className = "",
  isImageColumnAndNextItemIsImageColumn,
  isJustTextColumn,
  isColumnRowAndNeedsMoreSpacingBottom,
  ...props
}) {
  const isMobileLayout = useGlobalStore((s) => s.isMobileLayout);

  const widths = useMemo(() => {
    const totalWidth = data.columns.reduce(
      (acc, column) => acc + column.width,
      0
    );
    if (totalWidth > 100) return data.columns.map((c) => 100);
    return data.columns.map((c) => c.width);
  }, [data.columns]);

  const hasMultipleColumns = useMemo(() => {
    if (isMobileLayout) {
      // if one of the columns has a width less than 100, then we have multiple columns
      return data.columns.some((c) => c.mobileWidth < 100);
    }
    return data.columns.some((c) => c.width < 100);
  }, [data.columns, isMobileLayout]);

  return (
    <Wrapper
      // style={{ "--height": `${height}` }}
      $hasMultipleColumns={hasMultipleColumns}
      $isColumnRowAndNeedsMoreSpacingBottom={
        isColumnRowAndNeedsMoreSpacingBottom
      }
    >
      {data.columns.map(
        (
          {
            __typename,
            width: _width,
            mobileWidth: _mobileWidth,
            image,
            text,
            ...props
          },
          index
        ) => {
          let mobileWidth = _mobileWidth !== null ? _mobileWidth : 100;
          const width = isMobileLayout ? mobileWidth : _width;

          const style = {
            "--width": `${width}%`,
            marginBottom: isImageColumnAndNextItemIsImageColumn
              ? "150rem"
              : "0rem",
          };

          const isVideo =
            image?.url?.includes(".mp4") ||
            image?.url?.includes(".webm") ||
            image?.url?.includes(".m3u8") ||
            image?.url?.includes(".mov");

          if (isJustTextColumn) {
            style.margin = "250rem 0rem";
          }

          return (
            <div
              className={`column-row ${className} ${__typename}`}
              key={index}
              style={style}
            >
              {__typename === columnRowTypeNames.empty && null}

              {__typename === columnRowTypeNames.image && image && (
                <NineGLImageElement
                  className={
                    isVideo ? "column-row-image-video" : "column-row-image"
                  }
                  isLink={false}
                  key={index}
                  src={image.url}
                  isVideo={isVideo}
                  video={image.video}
                  offset={-0.5}
                />
              )}

              {__typename === columnRowTypeNames.text && text && (
                <ColumnTextItem
                  text={text}
                  width={width}
                  mobileWidth={mobileWidth}
                  {...props}
                />
              )}
            </div>
          );
        }
      )}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  ${util.innerWidth}

  margin-top: 100rem;
  margin-bottom: 100rem;
  height: fit-content;
  display: flex;
  flex-flow: row wrap;
  /* justify-content: center; */
  justify-content: space-between;

  gap: 30rem;

  position: relative;

  ${({ $isColumnRowAndNeedsMoreSpacingBottom }) =>
    $isColumnRowAndNeedsMoreSpacingBottom &&
    `
      margin-bottom: 250rem;
    `}

  .column-row-image-video {
    width: 100%;
    height: 100%;

    video {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
  .column-row-image {
    height: 100%;
  }

  .column-row {
    width: var(--width);
    min-width: var(--width);

    ${({ $hasMultipleColumns }) =>
      $hasMultipleColumns &&
      `
      width: calc(var(--width) - 30rem);
      min-width: calc(var(--width) - 30rem);
    `}

    position: relative;
    overflow: hidden;

    img {
      height: 100%;
      object-fit: cover;
    }
  }

  ${mediaMobile(`

    .EmptycolumnRecord {
      display: none;
    }
  `)}
`;
